/**
 * SidebarNav Hook
 *
 * Optimistic active-state highlighting for sidebar navigation.
 * Sets `data-selected` on the clicked link on mousedown (before the
 * server PubSub roundtrip completes), then re-applies it in updated()
 * to survive intermediate LiveView morphdom patches without blinking.
 */

// ── Types ─────────────────────────────────────────────────────

type NavControls = {
  readonly cleanup: () => void
  readonly reapply: () => void
}

type SidebarNavHook = {
  readonly el: HTMLElement
  _controls: NavControls | null
}

// ── Constants ─────────────────────────────────────────────────

const ITEM_LINK = '[data-component=".sidebar-item"] > a'

// ── Setup ─────────────────────────────────────────────────────

const setupNav = (root: HTMLElement): NavControls => {
  let optimisticHref: string | null = null

  // Use mousedown — fires ~100-150ms earlier than click (on press,
  // not release). The active style appears at the moment of contact.
  const onMouseDown = (e: Event): void => {
    const link = (e.target as Element).closest<HTMLElement>(ITEM_LINK)

    if (link?.hasAttribute('href')) {
      for (const el of root.querySelectorAll<HTMLElement>(
        `${ITEM_LINK}[data-selected]`
      )) {
        el.removeAttribute('data-selected')
      }

      link.setAttribute('data-selected', '')
      optimisticHref = link.getAttribute('href')
    }
  }

  // Re-apply optimistic selection after LiveView morphdom patches.
  // Runs synchronously before browser paint — prevents blink.
  const reapply = (): void => {
    if (!optimisticHref) {
      return
    }

    // Check if server confirmed (link with our href already has data-selected)
    const links = root.querySelectorAll<HTMLElement>(ITEM_LINK)

    for (const link of links) {
      if (
        link.getAttribute('href') === optimisticHref &&
        link.hasAttribute('data-selected')
      ) {
        // Server confirmed — stop overriding
        optimisticHref = null
        return
      }
    }

    // Server hasn't confirmed yet — re-apply our optimistic selection
    for (const link of links) {
      if (link.getAttribute('href') === optimisticHref) {
        link.setAttribute('data-selected', '')
      } else {
        link.removeAttribute('data-selected')
      }
    }
  }

  root.addEventListener('mousedown', onMouseDown)

  return {
    cleanup: () => root.removeEventListener('mousedown', onMouseDown),
    reapply
  }
}

// ── Hook ──────────────────────────────────────────────────────

export const SidebarNav = {
  mounted(this: SidebarNavHook) {
    this._controls = setupNav(this.el)
  },

  updated(this: SidebarNavHook) {
    this._controls?.reapply()
  },

  destroyed(this: SidebarNavHook) {
    this._controls?.cleanup()
  }
}
